import { useNavigate } from "react-router-dom";
import {
  validateBasicFields,
  validateBasicFieldsWithAddress,
  validatePhone,
  validateName,
  validateDocument,
  validateEmail,
  validateCep,
  showValidationError,
  showApiError
} from "./CustomerValidator";
 
 
import { cpfCnpjNoMask, telephoneNoMask } from "../../functions/mask";
import { createCustomer, removeOptInWhatsApp } from "./customerService";
import { buildCustomerFromResponse, buildCustomerObject } from "./BuildCustomer";

export const useCustomerHandlers = (
  customerForm: any,
  setLoading: (loading: boolean) => void,
  setCustomer: (customer: any) => void,
  shouldChangeOrderAddressCEP: (data: any) => void,
  updateCartWithCustomer: () => Promise<void>,
  navigate: ReturnType<typeof useNavigate>,
  pluginspace_id: string | undefined,
  getToken: () => Promise<string>
) => {

  const runAllValidations = async (phoneField: any, emailField: any): Promise<boolean> => {
    // Validar telefone
    const phoneValidation = validatePhone(customerForm.telephone, phoneField.mandatory && phoneField.visible);
    if (!phoneValidation.isValid) {
      if (phoneValidation.shouldClearField) customerForm.setTelephone("");
      return false;
    }

    // Validar nome
    const nameValidation = validateName(customerForm.name);
    if (!nameValidation.isValid) return false;

    // Validar documento
    const documentValidation = validateDocument(customerForm.cpfOrCnpj);
    if (!documentValidation.isValid) {
      if (documentValidation.shouldClearField) customerForm.setCpfOrCnpj("");
      return false;
    }

    // Validar email
    const emailValidation = validateEmail(customerForm.email, emailField.mandatory && emailField.visible);
    if (!emailValidation.isValid) {
      if (emailValidation.shouldClearField) customerForm.setEmail("");
      return false;
    }

    return true;
  };

  const runAllValidationsWithAddress = async (phoneField: any, emailField: any, findCep: (cep: string) => Promise<boolean>): Promise<boolean> => {
    // Executar validações básicas primeiro
    const basicValidationsOk = await runAllValidations(phoneField, emailField);
    if (!basicValidationsOk) return false;

    // Validar CEP
    const cepValidation = validateCep(customerForm.cep);
    if (!cepValidation.isValid) {
      if (cepValidation.shouldClearField) customerForm.setCep("");
      return false;
    }

    // Verificar se o CEP existe
    const cepOk = await findCep(customerForm.cep);
    if (!cepOk) return false;

    return true;
  };

  const processCustomerCreation = async (
    type: string,
    useWpp: boolean,
    inscEstadual: string,
    config: any,
    getOrderAddress: Function,
    customerValidationRegister?: boolean
  ) => {
    setLoading(true);
    
    try {
      const customerToSend = await buildCustomerObject(
        customerForm,
        type,
        useWpp,
        inscEstadual,
        config,
        getOrderAddress,
        customerValidationRegister
      );
      const token = await getToken();
      const response = await createCustomer(customerToSend, await getToken());

      if(!useWpp){
        try {
           await removeOptInWhatsApp(telephoneNoMask(customerForm.telephone), token)
        } catch (error) {
          console.log("Erro ao remover opt-in do WhatsApp:", error);
        }
      }

      await handleCustomerCreationResponse(response, useWpp, inscEstadual, customerValidationRegister);
    } catch (error) {
      setLoading(false);
      console.error('Erro ao processar pedido:', error);
      showApiError();
    }
  };

  const handleCustomerCreationResponse = async (response: any, useWpp: boolean, inscEstadual: string, customerValidationRegister?: boolean) => {
    setLoading(false);
    
    if (response.data.success === true) {
      const customer = buildCustomerFromResponse(response.data.dados, customerForm, useWpp, inscEstadual, customerValidationRegister);
      setCustomer(customer);
      shouldChangeOrderAddressCEP(response.data.dados);
      await updateCartWithCustomer();
      navigate(`/${pluginspace_id}/order`);
    } else {
      showApiError();
    }
  };

  // Handler para Food com Field Config
  const handleFoodWithFieldConfig = async (
    fieldsConfig: any[],
    type: string,
    useWpp: boolean,
    inscEstadual: string,
    config: any,
    getOrderAddress: Function
  ) => {
    const validation = validateBasicFields(customerForm, fieldsConfig, true);
    
    if (!validation.isValid) {
      showValidationError();
      return;
    }

    if (!await runAllValidations(validation.phoneField, validation.emailField)) {
      return;
    }

    await processCustomerCreation(type, useWpp, inscEstadual, config, getOrderAddress);
  };

  // Handler para Food sem Field Config
  const handleFoodWithoutFieldConfig = async (
    type: string,
    useWpp: boolean,
    inscEstadual: string,
    config: any,
    getOrderAddress: Function
  ) => {
    const validation = validateBasicFields(customerForm, [], false);
    
    if (!validation.isValid) {
      showValidationError();
      return;
    }

    if (!await runAllValidations(validation.phoneField, validation.emailField)) {
      return;
    }

    await processCustomerCreation(type, useWpp, inscEstadual, config, getOrderAddress);
  };

  // Handler com Field Config (com endereço)
  const handleWithFieldConfig = async (
    fieldsConfig: any[],
    type: string,
    useWpp: boolean,
    inscEstadual: string,
    config: any,
    getOrderAddress: Function,
    findCep: (cep: string) => Promise<boolean>
  ) => {
    const validation = validateBasicFieldsWithAddress(customerForm, fieldsConfig, true);
    
    if (!validation.isValid) {
      showValidationError();
      return;
    }

    if (!await runAllValidationsWithAddress(validation.phoneField, validation.emailField, findCep)) {
      return;
    }

    await processCustomerCreation(type, useWpp, inscEstadual, config, getOrderAddress);
  };

  // Handler sem Field Config (com endereço)
  const handleWithoutFieldConfig = async (
    type: string,
    useWpp: boolean,
    inscEstadual: string,
    config: any,
    getOrderAddress: Function,
    findCep: (cep: string) => Promise<boolean>,
    customerValidationRegister: boolean
  ) => {
    const validation = validateBasicFieldsWithAddress(customerForm, [], false);
    
    if (!validation.isValid) {
      showValidationError();
      return;
    }

    if (!await runAllValidationsWithAddress(validation.phoneField, validation.emailField, findCep)) {
      return;
    }

    await processCustomerCreation(type, useWpp, inscEstadual, config, getOrderAddress, customerValidationRegister);
  };

  

  return {
    handleFoodWithFieldConfig,
    handleFoodWithoutFieldConfig,
    handleWithFieldConfig,
    handleWithoutFieldConfig
  };
};